import type { ColumnFiltersState } from '@tanstack/react-table';
import type { RoyaltyChecksSearchParams } from './atoms';

export const DEFAULT_PARAMS: RoyaltyChecksSearchParams = {
  page: 1,
  pageSize: 100,
  sortAttribute: 'updatedAt',
  sortDirection: 'DESC',
};

export const SORT_FIELD_MAP: Record<string, string> = {
  name: 'name',
  checkName: 'name',
  checkNumber: 'checkNumber',
  checkAmount: 'checkAmount',
  checkIssueDate: 'checkIssueDate',
  source: 'source',
  createdAt: 'createdAt',
  updatedAt: 'updatedAt',
  createdByName: 'createdByName',
  payorName: 'payorName',
  payeeName: 'payeeName',
  lineItemCount: 'lineItemCount',
  propertyCount: 'propertyCount',
};

const COLUMN_FILTER_PARAM_KEYS: Array<keyof RoyaltyChecksSearchParams> = [
  'name',
  'checkNumber',
  'payorName',
  'payeeName',
  'createdByName',
  'checkAmountMin',
  'checkAmountMax',
  'lineItemCountMin',
  'lineItemCountMax',
  'propertyCountMin',
  'propertyCountMax',
  'sources',
  'checkIssueDateStart',
  'checkIssueDateEnd',
  'createdAtStart',
  'createdAtEnd',
  'updatedAtStart',
  'updatedAtEnd',
];

const readString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const readNumericRange = (value: unknown): { min?: number; max?: number } => {
  if (!Array.isArray(value) || value.length !== 2) return {};
  const [rawMin, rawMax] = value as [unknown, unknown];
  const min =
    typeof rawMin === 'number' && !Number.isNaN(rawMin) ? rawMin : undefined;
  const max =
    typeof rawMax === 'number' && !Number.isNaN(rawMax) ? rawMax : undefined;
  return { min, max };
};

const readDateRange = (value: unknown): { start?: string; end?: string } => {
  if (!Array.isArray(value) || value.length !== 2) return {};
  const [rawStart, rawEnd] = value as [unknown, unknown];
  return {
    start: typeof rawStart === 'string' && rawStart ? rawStart : undefined,
    end: typeof rawEnd === 'string' && rawEnd ? rawEnd : undefined,
  };
};

export const columnFiltersToParams = (
  filters: ColumnFiltersState
): Partial<RoyaltyChecksSearchParams> => {
  const params: Partial<RoyaltyChecksSearchParams> = {};
  for (const key of COLUMN_FILTER_PARAM_KEYS) {
    (params as Record<string, unknown>)[key] = undefined;
  }

  for (const filter of filters) {
    switch (filter.id) {
      case 'name':
      case 'checkName':
        params.name = readString(filter.value);
        break;
      case 'checkNumber':
        params.checkNumber = readString(filter.value);
        break;
      case 'payorName':
        params.payorName = readString(filter.value);
        break;
      case 'payeeName':
        params.payeeName = readString(filter.value);
        break;
      case 'createdByName':
        params.createdByName = readString(filter.value);
        break;
      case 'checkAmount': {
        const { min, max } = readNumericRange(filter.value);
        params.checkAmountMin = min;
        params.checkAmountMax = max;
        break;
      }
      case 'lineItemCount': {
        const { min, max } = readNumericRange(filter.value);
        params.lineItemCountMin = min;
        params.lineItemCountMax = max;
        break;
      }
      case 'propertyCount': {
        const { min, max } = readNumericRange(filter.value);
        params.propertyCountMin = min;
        params.propertyCountMax = max;
        break;
      }
      case 'source': {
        const list = Array.isArray(filter.value)
          ? (filter.value as Array<'Salesforce' | 'Provisioned'>)
          : [];
        params.sources = list.length > 0 ? list : undefined;
        break;
      }
      case 'checkIssueDate': {
        const { start, end } = readDateRange(filter.value);
        params.checkIssueDateStart = start;
        params.checkIssueDateEnd = end;
        break;
      }
      case 'createdAt': {
        const { start, end } = readDateRange(filter.value);
        params.createdAtStart = start;
        params.createdAtEnd = end;
        break;
      }
      case 'updatedAt': {
        const { start, end } = readDateRange(filter.value);
        params.updatedAtStart = start;
        params.updatedAtEnd = end;
        break;
      }
    }
  }

  return params;
};
